import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';

import { Icono } from './Iconos.jsx';
import {
  esDestinoActivo, obtenerDestinosDeLaBarra, obtenerExcedenteDelMenu,
} from '../nucleo/navegacion/registroDeSecciones.js';
import { MenuDeTiposDeMovimiento } from '../nucleo/navegacion/MenuDeTiposDeMovimiento.jsx';
import { obtenerContextoVigente, construirRutaDeAlta } from '../nucleo/navegacion/contextoDeNavegacion.js';
import { usePuntoDeEntrada, useRastreoDePasos } from '../nucleo/navegacion/puntoDeEntrada.js';
import { useSesion } from '../nucleo/sesion/ProveedorDeSesion.jsx';
import { formatearFecha, obtenerFechaDeNegocioHoy } from '../nucleo/fecha/fechaLocal.js';

/**
 * Armazon de la aplicacion con la navegacion principal.
 *
 * Reglas del prototipo aprobado que este componente hace cumplir:
 *   * SEIS destinos: los cinco de modulos mas Ajustes, SIEMPRE al final
 *     (RF-018, RF-031, D-07). El shell lo agrega al armar la barra; ningun
 *     modulo lo registra ni lo desplaza (RN-044b, RN-044c).
 *   * En escritorio, la misma navegacion es barra lateral y Ajustes queda al
 *     final tras un divisor (RN-044d).
 *   * El boton de captura flota sobre el contenido y no ocupa ninguno de los
 *     seis lugares (RF-020, RN-044e).
 *   * Sin sesion no hay barra y, por lo tanto, tampoco Ajustes (RN-044f).
 *
 * LO QUE EL INCREMENTO 09 CAMBIÓ AQUÍ, con la autorización expresa D-235 —primera
 * concesión sobre el armazón de navegación—:
 *   * LA FLECHA DE VOLVER YA NO EMPUJA (RF-152, RN-393). Era un enlace, y desde
 *     el resumen de una captura empujaba `/movimientos`, así que el botón atrás
 *     del teléfono devolvía al formulario que se creía dejado atrás (CR-02). Ahora
 *     es un botón que REEMPLAZA el paso actual: tocar ← y el botón atrás dejan de
 *     pelearse y nadie aterriza en un formulario que ya cerró. Se reemplaza el
 *     paso de la pantalla que se abandona, NUNCA el del origen (RN-396).
 *   * MODO DE REGRESO AL PUNTO DE ENTRADA (RF-151, D-249, RN-414). `volverA`
 *     conserva intacto su significado de «ruta padre fija» en sus 34 sitios de
 *     llamada (D-238, RN-418); el regreso a la pantalla desde la que se entró es
 *     un modo ADICIONAL y explícito que hoy pide SOLO Ajustes.
 *   * EL BOTÓN DE CAPTURA ABRE EL MENÚ DE TIPOS (RF-150, D-244) en sus dos
 *     tamaños de pantalla, en vez de llevar directo al gasto, y hereda el
 *     contexto del listado que esté a la vista (RN-411).
 *   * EL DESTINO ACTIVO SE DECIDE UNA SOLA VEZ (RN-422): las dos barras usan la
 *     función del núcleo, no dos criterios distintos (CR-05).
 */
export function Shell({ children, titulo, subtitulo, volverA, regresarAlPuntoDeEntrada = false }) {
  const { sesion } = useSesion();
  const ubicacion = useLocation();
  const navegar = useNavigate();
  const [enLinea, setEnLinea] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine !== false));
  const [menuDeCapturaAbierto, setMenuDeCapturaAbierto] = useState(false);

  /* Registro de pasos propios: es de dónde sale el punto de entrada (RN-414). */
  useRastreoDePasos();

  /*
   * El punto de entrada se calcula UNA VEZ al montar y no se mueve mientras la
   * persona está dentro (RN-421). Con el modo apagado —las otras 20 pantallas con
   * `volverA`— esto devuelve `volverA` tal cual: ninguna cambia de conducta.
   */
  const destinoDeLaFlecha = usePuntoDeEntrada(
    regresarAlPuntoDeEntrada ? ubicacion.pathname : null,
    volverA,
  );

  useEffect(() => {
    const conectar = () => setEnLinea(true);
    const desconectar = () => setEnLinea(false);
    window.addEventListener('online', conectar);
    window.addEventListener('offline', desconectar);
    return () => {
      window.removeEventListener('online', conectar);
      window.removeEventListener('offline', desconectar);
    };
  }, []);

  const destinos = obtenerDestinosDeLaBarra();
  const excedente = obtenerExcedenteDelMenu();
  const iniciales = (sesion?.nombre ?? '?')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((parte) => parte[0].toUpperCase())
    .join('');

  /*
   * RF-152, RN-393. La flecha es un REGRESO, no un enlace: reemplaza el paso
   * actual en vez de empujar uno nuevo. Casos borde resueltos de una sola forma y
   * sin ramas: se va al destino declarado por la pantalla —`volverA`, o el punto
   * de entrada si la pantalla pidió el modo—, así que (a) sin paso previo propio
   * funciona igual, y (b) la flecha NUNCA saca a la persona de la aplicación,
   * porque nunca entrega el control al historial del navegador.
   */
  function volver() {
    if (destinoDeLaFlecha) navegar(destinoDeLaFlecha, { replace: true });
  }

  /*
   * RN-411. Cada opción del menú arma su dirección con el contexto del listado
   * que está a la vista. Caso borde: si el menú se abre sobre una pantalla sin
   * contexto de listado —un detalle, Ajustes, Presupuesto—, las opciones se abren
   * SIN sugerencia y el regreso va al destino padre de siempre (RN-390c), que es
   * la conducta de hoy.
   *
   * LA CUENTA NO SE SUGIERE NUNCA (RF-153, RN-388): lo único que puede viajar como
   * sugerencia es la categoría, y solo a las capturas que la admiten.
   */
  function rutaDeLaOpcion(opcion) {
    const contexto = obtenerContextoVigente();
    if (!contexto) return opcion.ruta;

    const ruta = construirRutaDeAlta(opcion.ruta, {
      contextoDeRegreso: contexto.direccion,
      sugerencias: opcion.admiteCategoria ? contexto.sugerencias : null,
    });

    /*
     * D-478. El armazón TRANSPORTA la petición de reemplazar el paso; no la decide
     * ni sabe quién la pidió. La pide la pantalla que cede su paso al publicar su
     * contexto —hoy solo el resumen de guardado de una captura, RN-394—, y sin ella
     * se devuelve la cadena de siempre: un listado corriente sigue EMPUJANDO el paso
     * de la captura, como hasta ahora (RN-411).
     */
    return contexto.reemplazarElPaso === true ? { ruta, reemplazarElPaso: true } : ruta;
  }

  return (
    <div className="marco conbarra">
      {/* Barra lateral de escritorio: misma navegación, mismo orden (RN-044d). */}
      <aside className="lat">
        <div className="mk">Gastos Personales</div>
        {destinos.filter((d) => !d.esDelNucleo).map((destino) => (
          <NavLink
            key={destino.clave}
            to={destino.ruta}
            /* Mismo criterio que la barra inferior, una sola vez (RN-422). */
            className={esDestinoActivo(destino.ruta, ubicacion.pathname) ? 'on' : undefined}
            aria-current={esDestinoActivo(destino.ruta, ubicacion.pathname) ? 'page' : undefined}
          >
            <Icono nombre={destino.icono} /> {destino.etiqueta}
          </NavLink>
        ))}
        <div className="div" />
        <NavLink
          to="/ajustes"
          className={esDestinoActivo('/ajustes', ubicacion.pathname) ? 'on' : undefined}
          aria-current={esDestinoActivo('/ajustes', ubicacion.pathname) ? 'page' : undefined}
        >
          <Icono nombre="engrane" /> Ajustes
        </NavLink>

        {/* T-03 de CR-10 (RF-155, RN-448, D-256). El rótulo decía «+ Captura
            rápida», que era el nombre de la ruta a la que este control YA NO VA:
            ahora abre el menú de tipos. El rótulo nombra lo que hace y el control
            declara que despliega un menú y si está abierto. */}
        <button
          type="button"
          className="fabl"
          data-prueba="abrir-menu-de-captura-lateral"
          aria-haspopup="menu"
          aria-expanded={menuDeCapturaAbierto}
          onClick={() => setMenuDeCapturaAbierto((abierto) => !abierto)}
          style={{ border: 0, cursor: 'pointer', fontFamily: 'inherit', width: 'calc(100% - 12px)' }}
        >
          Capturar movimiento
        </button>
      </aside>

      <div className="cuerpo">
        <header className="app">
          {destinoDeLaFlecha
            ? (
              <button
                type="button"
                className="ico"
                onClick={volver}
                title="Volver"
                aria-label="Volver"
                data-prueba="boton-de-volver"
              >
                ←
              </button>
            )
            : <div className="avat" aria-hidden="true">{iniciales}</div>}

          <div className="crece">
            <b>{titulo ?? sesion?.nombre ?? 'Gastos Personales'}</b>
            <small>{subtitulo ?? `Sesión iniciada · Hoy ${formatearFecha(obtenerFechaDeNegocioHoy())}`}</small>
          </div>

          {/* Estado de conexión discreto: guardar exige conexión (RF-023). */}
          <span
            className="chip"
            style={{ background: '#ffffff26', color: '#fff', borderColor: '#ffffff55' }}
          >
            {enLinea ? 'En línea' : 'Sin conexión'}
          </span>

          <Link className="ico" to="/sesion" title="Sesión" aria-label="Sesión">◉</Link>
        </header>

        {children}

        {excedente.length > 0 && (
          <div className="info">
            Estas secciones no caben en la barra y viven en el menú; Ajustes conserva su
            lugar: {excedente.map((seccion) => seccion.etiqueta).join(' · ')}
          </div>
        )}
      </div>

      {/* El botón de captura NO consume ninguno de los seis destinos. T-04 de
          CR-10 (RF-155, RN-448, RNF-082): el nombre accesible era «Captura
          rápida» y era lo ÚNICO que oía quien usa lector de pantalla: se le
          anunciaba una captura y se le abría un menú. Ahora nombra la acción real
          y declara que despliega un menú y si está abierto. */}
      <button
        type="button"
        className="fab"
        data-prueba="abrir-menu-de-captura"
        title="Capturar movimiento"
        aria-label="Capturar movimiento"
        aria-haspopup="menu"
        aria-expanded={menuDeCapturaAbierto}
        onClick={() => setMenuDeCapturaAbierto((abierto) => !abierto)}
        style={{ cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}
      >
        +
      </button>

      <MenuDeTiposDeMovimiento
        abierto={menuDeCapturaAbierto}
        alCerrar={() => setMenuDeCapturaAbierto(false)}
        construirRuta={rutaDeLaOpcion}
      />

      <nav className="tab" aria-label="Navegación principal">
        {destinos.map((destino) => {
          /* UNA SOLA IMPLEMENTACIÓN de la regla, compartida con la lateral
             (RN-422). Antes era `startsWith` pelado. */
          const activo = esDestinoActivo(destino.ruta, ubicacion.pathname);
          return (
            <NavLink
              key={destino.clave}
              to={destino.ruta}
              aria-label={destino.etiqueta}
              aria-current={activo ? 'page' : undefined}
              className={activo ? 'on' : undefined}
            >
              <span className="i"><Icono nombre={destino.icono} activo={activo} /></span>
              <span className="et">{destino.etiquetaCorta}</span>
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}
